import React, { useState } from 'react';
import { QRScanner } from './QRScanner';

interface ValidationResult {
  success: boolean;
  message?: string;
  error?: string;
  coupon?: {
    id: number;
    title: string;
    description: string;
    discount: string;
    vendor_name: string;
    driver_name: string;
  };
  redeemed_at?: string;
}

export const ScannerPage: React.FC = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [lastScannedToken, setLastScannedToken] = useState<string>('');

  const handleScanSuccess = async (token: string) => {
    setLastScannedToken(token);
    setIsScanning(false);
    
    try {
      const response = await fetch('https://cf-api.b-9f2.workers.dev/api/qr/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      setValidationResult(data);

      // Auto-hide result after 5 seconds if successful
      if (data.success) {
        setTimeout(() => {
          setValidationResult(null);
          setIsScanning(true);
        }, 5000);
      }

    } catch (error) {
      setValidationResult({
        success: false,
        error: 'Erro de conexão',
        message: 'Não foi possível validar o código QR. Verifique sua conexão.'
      });
    }
  };

  const handleScanError = (error: string) => {
    setValidationResult({
      success: false,
      error: 'Erro de scanner',
      message: error
    });
  };

  const startScanning = () => {
    setIsScanning(true);
    setValidationResult(null);
  };

  const stopScanning = () => {
    setIsScanning(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="bg-blue-600 text-white rounded-t-lg p-6 text-center">
          <h1 className="text-2xl font-bold">📱 Scanner QR</h1>
          <p className="text-blue-100 mt-2">São Paulo - Cupons de Desconto</p>
        </div>

        <div className="bg-white rounded-b-lg shadow-lg">
          {/* Scanner Controls */}
          <div className="p-4 border-b">
            <div className="flex space-x-3">
              <button
                onClick={startScanning}
                disabled={isScanning}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  isScanning
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isScanning ? '📷 Escaneando...' : '▶️ Iniciar Scanner'}
              </button>
              
              <button
                onClick={stopScanning}
                disabled={!isScanning}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  !isScanning
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                ⏹️ Parar
              </button>
            </div>
          </div>

          {/* Scanner Area */}
          <div className="p-4">
            {isScanning ? (
              <QRScanner
                onScanSuccess={handleScanSuccess}
                onScanError={handleScanError}
                isActive={isScanning}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-6xl mb-4">📷</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Scanner Parado</h3>
                <p className="text-sm text-gray-600 text-center">
                  Clique em "Iniciar Scanner" para começar a escanear códigos QR
                </p>
              </div>
            )}
          </div>

          {/* Validation Result */}
          {validationResult && (
            <div className="p-4 border-t">
              {validationResult.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-4xl mr-4">✅</div>
                    <div className="flex-1">
                      <h3 className="text-green-800 font-bold text-lg mb-2">Cupom Validado!</h3>
                      {validationResult.coupon && (
                        <div className="space-y-2">
                          <div className="bg-white rounded p-3">
                            <h4 className="font-semibold text-gray-900">{validationResult.coupon.title}</h4>
                            <p className="text-sm text-gray-600">{validationResult.coupon.vendor_name}</p>
                            <p className="text-lg font-bold text-green-600 mt-2">{validationResult.coupon.discount}</p>
                            {validationResult.coupon.description && (
                              <p className="text-sm text-gray-700 mt-1">{validationResult.coupon.description}</p>
                            )}
                          </div>
                          <p className="text-xs text-green-700">
                            Driver: {validationResult.coupon.driver_name}
                          </p>
                        </div>
                      )}
                      <p className="text-green-600 text-sm mt-3">
                        {validationResult.message}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-4xl mr-4">❌</div>
                    <div className="flex-1">
                      <h3 className="text-red-800 font-bold text-lg">Cupom Inválido</h3>
                      <p className="text-red-600 text-sm mt-2">
                        {validationResult.message || validationResult.error}
                      </p>
                      <button
                        onClick={() => setValidationResult(null)}
                        className="mt-3 bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 transition-colors"
                      >
                        Tentar Novamente
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Instructions */}
          <div className="p-4 border-t bg-gray-50">
            <h4 className="font-medium text-gray-900 mb-2">📋 Como usar:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>1. Clique em "Iniciar Scanner"</li>
              <li>2. Aponte a câmera para o código QR</li>
              <li>3. O cupom será validado automaticamente</li>
              <li>4. Códigos QR expiram em 5 minutos</li>
            </ul>
          </div>

          {/* Debug Info */}
          {lastScannedToken && (
            <details className="p-4 border-t">
              <summary className="text-xs text-gray-500 cursor-pointer">Debug Info</summary>
              <div className="mt-2 bg-gray-100 p-2 rounded text-xs">
                <p>Último token escaneado:</p>
                <code className="break-all">{lastScannedToken.substring(0, 100)}...</code>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};